import { useMemo } from 'react';
import { useUserGeolocation } from '../hooks/useUserGeolocation';
import { distanceLabelFromCoords } from '../utils/distance';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, MapPin, MessageCircle, Clock3 } from 'lucide-react';
import { useCategories, useEstablishments, useItineraries } from '../hooks/useData';
import { getImageUrl } from '../utils/getImageUrl';
import { getCategoryPreset } from '../utils/categoryPresets';
import './Home.css';

type LocalCategory = {
    key: string;
    label: string;
    icon: React.ComponentType<{ size?: number }>;
    bg: string;
    color: string;
};

const FALLBACK_CATEGORY_DEFS: { key: string; label: string }[] = [
    { key: 'comer-beber', label: 'Comer & Beber' },
    { key: 'compras', label: 'Compras' },
    { key: 'pet', label: 'Pet' },
    { key: 'academias', label: 'Academias' },
    { key: 'passeios', label: 'Passeios' },
    { key: 'vida-noturna', label: 'Vida Noturna' },
    { key: 'pontos-turisticos', label: 'Pontos Turisticos' },
    { key: 'saude', label: 'Saude' },
    { key: 'eventos', label: 'Eventos' },
];

const FALLBACK_CATEGORIES: LocalCategory[] = FALLBACK_CATEGORY_DEFS.map(({ key, label }) => {
    const preset = getCategoryPreset(key, label);
    return {
        key,
        label,
        icon: preset.icon,
        bg: preset.bg,
        color: preset.color,
    };
});

const FALLBACK_ITINERARIES = [
    { title: 'Manha perfeita na praia', subtitle: 'Cafe da manha, praia e almoco pe na areia', duration: '4h' },
    { title: 'Roteiro romantico', subtitle: 'Passeio no por do sol e jantar especial', duration: '5h' },
    { title: 'Roteiro gastronomico', subtitle: 'Descubra os melhores sabores de Ubatuba', duration: '6h' },
    { title: 'Dia de chuva', subtitle: 'Atividades indoor e cafes aconchegantes', duration: '3h' },
];

const ITINERARY_GRADIENTS = [
    'linear-gradient(135deg, #FFB703 0%, #FB8500 100%)',
    'linear-gradient(135deg, #F973AE 0%, #EC4899 100%)',
    'linear-gradient(135deg, #A855F7 0%, #7C3AED 100%)',
    'linear-gradient(135deg, #60A5FA 0%, #06B6D4 100%)',
];

const Home = () => {
    const navigate = useNavigate();
    const userFix = useUserGeolocation();
    const userPoint = userFix ? { lat: userFix.lat, lng: userFix.lng } : null;
    const { data: categories } = useCategories();
    const { data: nearbyPlaces, loading: loadingPlaces } = useEstablishments();
    const { data: itineraries } = useItineraries();

    const renderCategories = useMemo(() => {
        if (!categories.length) return FALLBACK_CATEGORIES;

        return categories.map((cat) => {
            const p = getCategoryPreset(cat.slug || '', cat.label || '');
            return {
                key: cat.slug || cat.id,
                label: cat.label,
                icon: p.icon,
                bg: p.bg,
                color: p.color,
            };
        });
    }, [categories]);

    const renderPlaces = useMemo(() => nearbyPlaces.slice(0, 3), [nearbyPlaces]);
    const renderItineraries = useMemo(
        () => (itineraries.length ? itineraries.slice(0, 4) : FALLBACK_ITINERARIES),
        [itineraries],
    );

    return (
        <div className="home-page">
            <section className="home-hero">
                <span className="hero-pill">Facilita Pass</span>
                <h1>Bem-vindo a Ubatuba!</h1>
                <p>
                    Aproveite conosco o melhor da gastronomia, turismo e cultura local,
                    com descontos imperdiveis e um roteiro totalmente personalizado
                </p>
                <button type="button" className="hero-cta" onClick={() => navigate('/benefits')}>
                    Ver Beneficios Agora
                </button>
            </section>

            <section className="home-categories-card">
                <h2>Explorar por Categoria</h2>
                <div className="categories-grid">
                    {renderCategories.map((item) => {
                        const Icon = item.icon;
                        return (
                            <button
                                type="button"
                                key={item.key}
                                className="category-item"
                                onClick={() => navigate(`/categories?type=${item.key}`)}
                            >
                                <span className="category-icon-wrap" style={{ backgroundColor: item.bg, color: item.color }}>
                                    <Icon size={24} />
                                </span>
                                <span className="category-item-label">{item.label}</span>
                            </button>
                        );
                    })}
                </div>
            </section>

            <section className="home-section">
                <div className="home-section-header">
                    <h2>Locais Proximos a Voce</h2>
                    <button type="button" onClick={() => navigate('/categories')}>
                        Ver todos <ChevronRight size={18} />
                    </button>
                </div>

                <div className="nearby-list">
                    {loadingPlaces && (
                        <>
                            <div className="nearby-card nearby-card-skeleton" />
                            <div className="nearby-card nearby-card-skeleton" />
                            <div className="nearby-card nearby-card-skeleton" />
                        </>
                    )}

                    {!loadingPlaces && !renderPlaces.length && (
                        <div className="nearby-empty">Nenhum local encontrado no momento.</div>
                    )}

                    {!loadingPlaces && renderPlaces.map((place) => (
                        <button
                            key={place.id}
                            type="button"
                            className="nearby-card"
                            onClick={() => navigate(`/establishment/${place.id}`)}
                        >
                            <div className="nearby-image-wrap">
                                <img src={getImageUrl(place.image)} alt={place.name} className="nearby-image" />
                                {place.discount && <span className="nearby-discount">{place.discount}</span>}
                            </div>
                            <div className="nearby-content">
                                <h3>{place.name}</h3>
                                <span className="nearby-partner">Parceiro Facilita Pass</span>
                                <span className="nearby-distance">
                                    <MapPin size={16} />
                                    {distanceLabelFromCoords(userPoint, place.latitude, place.longitude, place.distance)}
                                </span>
                            </div>
                        </button>
                    ))}
                </div>
            </section>

            <button type="button" className="assistant-banner" onClick={() => navigate('/assistente')}>
                <span className="assistant-icon">
                    <MessageCircle size={22} />
                </span>
                <span className="assistant-copy">
                    <strong>Assistente Virtual</strong>
                    <small>Tire suas duvidas e encontre o que precisa</small>
                </span>
                <ChevronRight size={20} />
            </button>

            <section className="home-section">
                <h2 className="home-section-title">Roteiros Sugeridos</h2>
                <div className="itinerary-grid">
                    {renderItineraries.map((item, index) => {
                        const duration = 'duration' in item ? (item as any).duration : FALLBACK_ITINERARIES[index % 4].duration;
                        const coverImage = 'cover_image' in item ? (item as any).cover_image : null;
                        const hasId = 'id' in item;
                        return (
                            <button
                                type="button"
                                key={`${item.title}-${index}`}
                                className="itinerary-card"
                                onClick={() => hasId ? navigate(`/roteiro/${(item as any).id}`) : null}
                            >
                                <div
                                    className="itinerary-cover"
                                    style={coverImage
                                        ? { backgroundImage: `url(${getImageUrl(coverImage)})`, backgroundSize: 'cover', backgroundPosition: 'center' }
                                        : { background: ITINERARY_GRADIENTS[index % 4] }
                                    }
                                >
                                    {duration && (
                                        <span className="itinerary-time">
                                            <Clock3 size={14} />
                                            {duration}
                                        </span>
                                    )}
                                </div>
                                <div className="itinerary-body">
                                    <h3>{item.title}</h3>
                                    <p>{item.subtitle}</p>
                                </div>
                            </button>
                        );
                    })}
                </div>
            </section>
        </div>
    );
};

export default Home;
